/**
 * Permission checking composable
 * Centralized permission logic for role-based access control
 */

import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'

export function usePermissions() {
  const authStore = useAuthStore()
  const { error: showError } = useToast()

  // Get user's permissions
  const userPermissions = computed(() => {
    return authStore.userProfile?.permissions || []
  })

  // Get user's role
  const userRole = computed(() => {
    return authStore.userProfile?.role?.toLowerCase() || 'tester'
  })

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check (e.g., 'projects:write')
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!authStore.isAuthenticated) {
      return false
    }
    
    // Super Admin has all permissions
    if (userRole.value === 'super_admin') {
      return true
    }
    
    // Admin has most permissions including role management
    if (userRole.value === 'admin') {
      return true
    }
    
    // QA Manager has permissions for features, bugs, test cases, and test runs
    if (userRole.value === 'qa_manager') {
      const qaManagerPermissions = [
        'features:read', 'features:write', 'features:delete',
        'bugs:read', 'bugs:write', 'bugs:delete', 'bugs:close',
        'test_cases:read', 'test_cases:write', 'test_cases:delete',
        'test_runs:read', 'test_runs:write', 'test_runs:delete',
        'projects:read', 'projects:write',
        'teams:read',
        'users:read',
        'reports:read', 'reports:write',
        'folders:read', 'folders:write', 'folders:delete',
        'comments:read', 'comments:write', 'comments:delete',
        'settings:read'
      ]
      return qaManagerPermissions.includes(permission)
    }
    
    // Developer and Tester roles have permissions for features, bugs, test cases, and test runs
    if (userRole.value === 'developer' || userRole.value === 'tester') {
      const fullAccessPermissions = [
        'features:read', 'features:write', 'features:delete',
        'bugs:read', 'bugs:write', 'bugs:delete',
        'test_cases:read', 'test_cases:write', 'test_cases:delete',
        'test_runs:read', 'test_runs:write', 'test_runs:delete',
        'projects:read',
        'reports:read',
        'folders:read',
        'comments:read', 'comments:write'
      ]
      return fullAccessPermissions.includes(permission)
    }
    
    return userPermissions.value.includes(permission)
  }

  /**
   * Check if user can promote others to Super Admin
   * @returns {boolean}
   */
  const canPromoteToSuperAdmin = () => {
    if (!authStore.isAuthenticated) {
      return false
    }
    
    // Only Super Admin can promote others to Super Admin
    return userRole.value === 'super_admin'
  }

  /**
   * Check if user has ANY of the specified permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  const hasAnyPermission = (permissions) => {
    if (!authStore.isAuthenticated) {
      return false
    }
    
    if (userRole.value === 'super_admin' || userRole.value === 'admin') {
      return true
    }
    
    return permissions.some(permission => userPermissions.value.includes(permission))
  }

  /**
   * Check if user has ALL of the specified permissions
   * @param {string[]} permissions - Array of permissions to check
   * @returns {boolean}
   */
  const hasAllPermissions = (permissions) => {
    if (!authStore.isAuthenticated) {
      return false
    }
    
    if (userRole.value === 'super_admin' || userRole.value === 'admin') {
      return true
    }
    
    return permissions.every(permission => userPermissions.value.includes(permission))
  }

  /**
   * Check permission and show error if denied
   * @param {string} permission - Permission to check
   * @param {string} customMessage - Custom error message
   * @returns {boolean}
   */
  const requirePermission = (permission, customMessage = null) => {
    if (!hasPermission(permission)) {
      const message = customMessage || `You don't have permission to perform this action. Required: ${permission}`
      showError(message)
      return false
    }
    return true
  }


  /**
   * Role-based permission checks
   */
  const can = {
    // Users
    viewUsers: () => hasPermission('users:read'),
    createUsers: () => hasPermission('users:write'),
    editUsers: () => hasPermission('users:write'),
    deleteUsers: () => hasPermission('users:delete'),
    manageUserRoles: () => hasPermission('users:manage_roles'),
    promoteToSuperAdmin: () => canPromoteToSuperAdmin(),
    
    // Teams
    viewTeams: () => hasPermission('teams:read'),
    createTeams: () => hasPermission('teams:write'),
    editTeams: () => hasPermission('teams:write'),
    deleteTeams: () => hasPermission('teams:delete'),
    
    // Projects
    viewProjects: () => hasPermission('projects:read'),
    createProjects: () => hasPermission('projects:write'),
    editProjects: () => hasPermission('projects:write'),
    deleteProjects: () => hasPermission('projects:delete'),
    createPrivateProjects: () => hasPermission('projects:create_private'),
    
    // Test Cases
    viewTestCases: () => hasPermission('test_cases:read'),
    createTestCases: () => hasPermission('test_cases:write'),
    editTestCases: () => hasPermission('test_cases:write'),
    deleteTestCases: () => hasPermission('test_cases:delete'),
    
    // Test Runs
    viewTestRuns: () => hasPermission('test_runs:read'),
    createTestRuns: () => hasPermission('test_runs:write'),
    editTestRuns: () => hasPermission('test_runs:write'),
    executeTestRuns: () => hasPermission('test_runs:execute'),
    deleteTestRuns: () => hasPermission('test_runs:delete'),
    
    // Bugs
    viewBugs: () => hasPermission('bugs:read'),
    createBugs: () => hasPermission('bugs:write'),
    editBugs: () => hasPermission('bugs:write'),
    closeBugs: () => hasPermission('bugs:close'),
    deleteBugs: () => hasPermission('bugs:delete'),
    
    // Reports
    viewReports: () => hasPermission('reports:read'),
    createReports: () => hasPermission('reports:write'),
    editReports: () => hasPermission('reports:write'),
    deleteReports: () => hasPermission('reports:delete'),
    
    // Folders
    viewFolders: () => hasPermission('folders:read'),
    createFolders: () => hasPermission('folders:write'),
    editFolders: () => hasPermission('folders:write'),
    deleteFolders: () => hasPermission('folders:delete'),
    
    // Features
    viewFeatures: () => hasPermission('features:read'),
    createFeatures: () => hasPermission('features:write'),
    editFeatures: () => hasPermission('features:write'),
    deleteFeatures: () => hasPermission('features:delete'),
    
    // Comments
    viewComments: () => hasPermission('comments:read'),
    createComments: () => hasPermission('comments:write'),
    editComments: () => hasPermission('comments:write'),
    deleteComments: () => hasPermission('comments:delete'),
    
    // Settings
    viewSettings: () => hasPermission('settings:read'),
    editSettings: () => hasPermission('settings:write'),
  }

  /**
   * Check if user is a specific role
   */
  const isRole = {
    superAdmin: computed(() => userRole.value === 'super_admin'),
    admin: computed(() => userRole.value === 'admin'),
    qaManager: computed(() => userRole.value === 'qa_manager'),
    tester: computed(() => userRole.value === 'tester'),
    developer: computed(() => userRole.value === 'developer'),
  }

  return {
    // Core permission checking
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    requirePermission,
    canPromoteToSuperAdmin,
    
    // Convenience methods
    can,
    isRole,
    
    // State
    userPermissions,
    userRole,
  }
}

